/**
 * PhysicsDebugRenderer - wireframe visualization of EVERY physics
 * collider via Rapier's own debug-render pipeline (world.debugRender()):
 * the chair's compound hulls/trimeshes, the ground slab and the figure's
 * capsules all appear, exactly where the solver sees them.
 *
 * The line buffer is emitted in physics coordinates, which are the
 * pivot-LOCAL frame - the debug object is added via sceneManager.add(),
 * which parents it to the SceneRotationPivot, so the wireframes coincide
 * with the meshes they were built from (or make it obvious when they
 * don't, which is the point).
 *
 * Toggled with Shift+H; update() is called from the animate loop every
 * frame while enabled (the buffers re-pose with the simulation).
 */

import * as THREE from 'three';

export class PhysicsDebugRenderer {
    constructor(app) {
        this.app = app;
        this.enabled = false;
        this.lines = null;
        this._material = new THREE.LineBasicMaterial({
            vertexColors: true,
            depthTest: false,
            transparent: true,
            opacity: 0.9
        });
    }

    /**
     * Enable/disable the debug draw. Returns the new state.
     */
    setEnabled(enabled) {
        this.enabled = !!enabled;
        if (!this.enabled) {
            this._clear();
        } else {
            this.update();
        }
        return this.enabled;
    }

    toggle() {
        return this.setEnabled(!this.enabled);
    }

    /**
     * Per-frame update (call from the animate loop while enabled).
     * world.debugRender() returns fresh line-segment buffers each call:
     * vertices [x,y,z per vertex, 2 vertices per segment] and colors
     * [r,g,b,a per vertex].
     */
    update() {
        if (!this.enabled) return;

        const world = this.app.physicsWorld?.world;
        if (!world?.debugRender) return;

        const { vertices, colors } = world.debugRender();

        if (!this.lines) {
            const geometry = new THREE.BufferGeometry();
            this.lines = new THREE.LineSegments(geometry, this._material);
            this.lines.name = 'PhysicsDebugColliders';
            this.lines.renderOrder = 10000; // draw over everything
            this.lines.frustumCulled = false;
            // sceneManager.add parents to the rotation pivot - the physics frame
            this.app.sceneManager.add(this.lines);
        }

        const geometry = this.lines.geometry;
        const vertexCount = vertices.length / 3;

        // Buffers change size whenever bodies appear/disappear - reallocate
        // then, otherwise update in place
        if (geometry.getAttribute('position')?.count !== vertexCount) {
            geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
            // colors arrive rgba; three's vertexColors wants rgb
            const rgb = new Float32Array(vertexCount * 3);
            for (let i = 0; i < vertexCount; i++) {
                rgb[i * 3] = colors[i * 4];
                rgb[i * 3 + 1] = colors[i * 4 + 1];
                rgb[i * 3 + 2] = colors[i * 4 + 2];
            }
            geometry.setAttribute('color', new THREE.BufferAttribute(rgb, 3));
        } else {
            geometry.getAttribute('position').copyArray(vertices);
            geometry.getAttribute('position').needsUpdate = true;
            const colorAttr = geometry.getAttribute('color');
            for (let i = 0; i < vertexCount; i++) {
                colorAttr.array[i * 3] = colors[i * 4];
                colorAttr.array[i * 3 + 1] = colors[i * 4 + 1];
                colorAttr.array[i * 3 + 2] = colors[i * 4 + 2];
            }
            colorAttr.needsUpdate = true;
        }
    }

    /**
     * Remove and dispose the debug object
     */
    _clear() {
        if (!this.lines) return;
        this.lines.parent?.remove(this.lines);
        this.lines.geometry.dispose();
        this.lines = null;
    }
}
